import { useMemo, useState } from "react";
import { ArrowRight, Briefcase, Building2, Layers, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { filterBuildings } from "@/lib/inventory-scope";
import type { BuildingStatus } from "@/lib/api/building";
import { inputGlowInner, inputGlowWrap, cardSurface } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

export type ScopePickTab = "buildings" | "groups";

const statusTone: Record<BuildingStatus, { dot: string; label: string }> = {
  ok: { dot: "bg-success", label: "Optimoitu" },
  warn: { dot: "bg-warning", label: "Potentiaalia" },
  alert: { dot: "bg-destructive", label: "Toimi nyt" },
};

const eur = (n: number) =>
  new Intl.NumberFormat("fi-FI", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );

export function ScopePickDialog({
  tab,
  onClose,
}: {
  tab: ScopePickTab;
  onClose: () => void;
}) {
  const {
    buildings,
    groups,
    filter,
    activeGroup,
    setBuildingId,
    setGroupId,
    reset,
  } = useInventoryFilter();
  const [query, setQuery] = useState("");

  const buildingRows = useMemo(() => {
    const scoped = filterBuildings(
      buildings,
      filter,
      activeGroup?.building_ids ?? null,
    );
    const q = query.trim().toLowerCase();
    const rows = q
      ? scoped.filter(
          (b) =>
            b.name.toLowerCase().includes(q) ||
            b.city.toLowerCase().includes(q),
        )
      : scoped;
    return [...rows].sort((a, b) => a.name.localeCompare(b.name, "fi"));
  }, [buildings, filter, activeGroup, query]);

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="flex flex-col gap-3 w-[640px] max-w-[92vw] h-[70vh] max-h-[640px]">
        <DialogHeader className="shrink-0">
          <DialogTitle>Valitse tarkastelu</DialogTitle>
        </DialogHeader>

        <button
          type="button"
          onClick={() => {
            reset();
            onClose();
          }}
          className={cn(
            cardSurface,
            "w-full flex items-center gap-2.5 p-2.5 text-left transition hover:border-primary/30 shrink-0",
          )}
        >
          <span className="h-7 w-7 rounded-md bg-primary/10 grid place-items-center shrink-0">
            <Layers className="h-3.5 w-3.5 text-primary" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium">Kaikki kiinteistöt</span>
            <span className="block text-[11px] text-muted-foreground">
              Koko kiinteistökanta · tyhjennä rajaukset
            </span>
          </span>
        </button>

        <Tabs
          key={tab}
          defaultValue={tab}
          className="flex-1 min-h-0 flex flex-col"
        >
          <TabsList className="self-start mb-3">
            <TabsTrigger value="buildings">
              <Building2 className="h-3.5 w-3.5 mr-1.5" />
              Kiinteistöt
            </TabsTrigger>
            <TabsTrigger value="groups">
              <Layers className="h-3.5 w-3.5 mr-1.5" />
              Salkut
            </TabsTrigger>
          </TabsList>

          <TabsContent value="buildings" className="flex-1 min-h-0 mt-0">
            <div className="h-full flex flex-col min-h-0 gap-3">
              <div className={inputGlowWrap}>
                <div
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-1.5",
                    inputGlowInner,
                  )}
                >
                  <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Hae nimellä tai paikkakunnalla…"
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
                {buildingRows.length === 0 && (
                  <div className="px-2 py-6 text-center text-[11px] text-muted-foreground">
                    Ei kohteita nykyisellä rajauksella.
                  </div>
                )}
                {buildingRows.map((b) => {
                  const tone = statusTone[b.status];
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => {
                        setBuildingId(b.id);
                        onClose();
                      }}
                      className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left border border-transparent hover:bg-surface transition"
                    >
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full shrink-0",
                          tone.dot,
                        )}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">
                          {b.name}
                        </span>
                        <span className="block text-[11px] text-muted-foreground truncate">
                          {b.city} · {tone.label}
                        </span>
                      </span>
                      {b.optimization_potential_eur_month != null && (
                        <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                          {eur(b.optimization_potential_eur_month)} €/kk
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="groups" className="flex-1 min-h-0 mt-0">
            <div className="h-full overflow-y-auto -mx-1 px-1 space-y-1">
              {groups.length === 0 && (
                <div className="px-2 py-6 text-center text-[11px] text-muted-foreground">
                  Ei salkkuja.
                </div>
              )}
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    setGroupId(g.id);
                    onClose();
                  }}
                  className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left border border-transparent hover:bg-surface transition"
                >
                  <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">
                      {g.name}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {g.building_ids.length} kiinteistöä
                    </span>
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
